import React, { useEffect, useState, useContext } from "react";
import { FaEye, FaTrash, FaCheck, FaPencilAlt, FaPrint } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../Loader/Loader";
import { deleteForm, getAdmissions, approveForm, promoteForm } from "../api/Api";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from "../Loader/Pagination";
import { FaPencil } from "react-icons/fa6";
import { getAcademicYear, getNextAcademicYear, generateAcademicYears } from "../utils/academicYearUtils";
import { AuthContext } from "../utils/AuthContext";
import { Search, Calendar, Filter } from "lucide-react";

const Table = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [feeAmount, setFeeAmount] = useState('');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [formToApprove, setFormToApprove] = useState(null);
  const [approving, setApproving] = useState(false);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("role");
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [formToPromote, setFormToPromote] = useState(null);
  const [newClass, setNewClass] = useState('');
  const [newFeeAmount, setNewFeeAmount] = useState('');
  const [newAcademicYear, setNewAcademicYear] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAdmissionFor, setSelectedAdmissionFor] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClassFilter, setSelectedClassFilter] = useState('');
  const handleAdmissionForChange = (option) => {
    setSelectedAdmissionFor(option);
    setNewClass(option);
  };


  const fetchData = async () => {
    try {
      const response = await getAdmissions();
      if (response.success) {
        const dataList = response.data.map((doc, index) => ({
          id: doc._id,
          sno: index + 1,
          fullName: doc.particularsOfChild?.fullName || "N/A",
          dateOfAdmission: doc.dateOfAdmission || "N/A",
          FatherName: doc.particularsOfParents?.FatherName || doc.particularsOfParents?.FatherMobile || "N/A",
          MotherName: doc.particularsOfParents?.MotherName || doc.particularsOfParents?.MotherMobile || "N/A",
          FatherMobile: doc.particularsOfParents?.FatherMobile || "N/A",
          MotherMobile: doc.particularsOfParents?.MotherMobile || "N/A",
          admissionFor: doc.admissionFor || "N/A",
          academicYear: doc.academicYear || "N/A",
          isApproved: doc.isApproved || false,
          address: `${doc.particularsOfChild?.houseNo || ""} ${doc.particularsOfChild?.buildingName || ""} ${doc.particularsOfChild?.streetName || ""} ${doc.particularsOfChild?.city || ""}  ${doc.particularsOfChild?.pincode || ""}`,
        }));
        setData(dataList.reverse());
      } else {
        console.log("No admissions found.");
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    } finally {
      setLoading(false);
    }
  };

  const { academicYear, setAcademicYear } = useContext(AuthContext);
  const academicYears = generateAcademicYears();

  useEffect(() => {
    fetchData();
  }, [academicYear]);

  const handleView = (item) => {
    navigate(`/form/${item.id}`);
  };
  const handleEdit = (id) => {
    navigate(`/form/${id}`);
  };
  const handleDelete = async () => {
    if (formToDelete) {
      try {
        await deleteForm(formToDelete);
        setData((prevData) => prevData.filter((item) => item.id !== formToDelete));
        setShowDeleteDialog(false);
        toast.success("Admission deleted successfully!");
        fetchData();
      } catch (error) {
        console.error("Error deleting form:", error);
        toast.error("Failed to delete admission.");
      }
    }
  };

  const handlePromote = (item) => {
    setFormToPromote(item.id);
    // Auto-fill academic year based on admission date
    const currentYear = getAcademicYear(item.dateOfAdmission);
    const nextYear = getNextAcademicYear(currentYear);
    setNewAcademicYear(nextYear); // Set to next academic year by default
    setShowPromoteDialog(true);
  };

  const handleApprove = (id) => {
    setFormToApprove(id);
    setShowApproveDialog(true);
  };

  const handlePrintAll = () => {
    const formIds = data.map(item => item.id).join(',');
    if (formIds) {
      navigate(`/print-all-forms?ids=${formIds}`);
    } else {
      toast.error("No forms to print.");
    }
  };

  const handlePrintSingleForm = (id) => {
    navigate(`/print-form/${id}`);
  };

  const confirmApproval = async () => {
    if (formToApprove && feeAmount) {
      setApproving(true);
      try {
        console.log("Approving form:", formToApprove, "with fee amount:", feeAmount);
        const response = await approveForm(formToApprove, { feeAmount });
        if (response.success) {
          setData((prevData) =>
            prevData.map((item) =>
              item.id === formToApprove ? { ...item, isApproved: true } : item
            )
          );
          toast.success("Admission approved successfully!");
          fetchData();
        }
      } catch (error) {
        console.error("Error approving form:", error);
        toast.error("Failed to approve admission.");
      } finally {
        setShowApproveDialog(false);
        setFeeAmount('');
        setFormToApprove(null);
        setApproving(false);
      }
    } else {
      toast.error("Please enter a valid fee amount.");
    }
  };

  const confirmPromotion = async () => {
    if (formToPromote && newClass && newFeeAmount && newAcademicYear) {
      try {
        const response = await promoteForm(formToPromote, { newClass, newFeeAmount, newAcademicYear });
        if (response.success) {
          setData((prevData) =>
            prevData.map((item) =>
              item.id === formToPromote ? { ...item, admissionFor: newClass, feeAmount: newFeeAmount } : item
            )
          );
          toast.success("Admission promoted successfully!");
          fetchData();
        }
      } catch (error) {
        console.error("Error promoting form:", error);
        toast.error("Failed to promote admission.");
      } finally {
        setShowPromoteDialog(false);
        setNewClass('');
        setNewFeeAmount('');
        setNewAcademicYear('');
        setFormToPromote(null);
      }
    } else {
      toast.error("Please enter valid class, fee amount, and academic year.");
    }
  };

  const filteredData = data.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    const admissionDate = new Date(item.dateOfAdmission);
    const start = new Date(startDate);
    const end = new Date(endDate);

    const isWithinDateRange = (!startDate || admissionDate >= start) && (!endDate || admissionDate <= end);

    const matchesSearch =
      item.fullName.toLowerCase().includes(searchLower) ||
      item.admissionFor.toLowerCase().includes(searchLower) ||
      item.FatherName.toLowerCase().includes(searchLower) ||
      item.MotherName.toLowerCase().includes(searchLower) ||
      item.FatherMobile.includes(searchQuery) ||
      item.MotherMobile.includes(searchQuery);

    const matchesClass =
      !selectedClassFilter ||
      item.admissionFor === selectedClassFilter;

    return isWithinDateRange && matchesSearch && matchesClass;
  });


  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const options = [...new Set(data.map((form) => form.admissionFor))];

  return (
    <div className="p-4 max-w-screen-xl mx-auto">
      <ToastContainer />

      {/* Top Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">

        {/* Header Row */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">

          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-[#16bab1]">
              Admissions Table
            </h1>
            <p className="text-sm text-gray-500">
              Manage all admission records here
            </p>
          </div>

          {/* Search */}
          <div className="w-full xl:w-[280px]">
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search admissions..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-[#16bab1]"
            />
          </div>

          {/* Academic Year Filter */}
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
            <Filter size={16} className="text-[#16bab1]" />
            <div className="flex flex-col">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Year</label>
              <select
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                className="bg-transparent text-sm font-semibold text-gray-700 outline-none pr-4 appearance-none cursor-pointer"
              >
                <option value="">Overall View</option>
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Filter */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent text-sm font-medium outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent text-sm font-medium outline-none"
                />
              </div>
            </div>
          </div>

          {/* Add Button */}
          <div>
            <Link to="/admission">
              <button className="bg-[#16bab1] hover:bg-[#139a93] transition-all duration-200 px-5 py-2.5 rounded-lg text-white font-semibold shadow-sm whitespace-nowrap">
                + Add Admission
              </button>
            </Link>
          </div>
        </div>

        {/* Class Filter */}
        <div className="flex flex-wrap gap-2 mt-5">

          <button
            onClick={() => setSelectedClassFilter('')}
            className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200
        ${selectedClassFilter === ''
                ? 'bg-[#16bab1] text-white border-[#16bab1] shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-[#16bab1] hover:text-[#16bab1]'
              }`}
          >
            All
          </button>

          {options.map((option) => (
            <button
              key={option}
              onClick={() => setSelectedClassFilter(option)}
              className={`px-4 py-2 rounded-full border text-sm font-medium capitalize transition-all duration-200
          ${selectedClassFilter === option
                  ? 'bg-[#16bab1] text-white border-[#16bab1] shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-[#16bab1] hover:text-[#16bab1]'
                }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Loader or Table */}
      {loading ? (
        <Loader />
      ) : currentData.length === 0 ? (
        <div className="text-[#16bab1] text-center mt-8">
          No admissions right now.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200 shadow-sm">
          <table className="min-w-[768px] w-full text-sm sm:text-base">
            <thead className="bg-[#16bab1] text-white">
              <tr>
                <th className="px-4 py-2 border">S.No</th>
                <th className="px-4 py-2 border">Name</th>
                <th className="px-4 py-2 border">Admission Date</th>
                <th className="px-4 py-2 border">Class</th>
                <th className="px-4 py-2 border">Year</th>
                <th className="px-4 py-2 border">Father</th>
                <th className="px-4 py-2 border">Mother</th>
                <th className="px-4 py-2 border">Approval</th>
                {userRole === 'admin' && (
                  <th className="px-4 py-2 border">Promote</th>
                )}
                <th className="px-4 py-2 border">View</th>
              </tr>
            </thead>
            <tbody>
             {currentData.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50">
<td className="px-4 py-2 border text-[#16bab1] font-semibold">

  {filteredData.length - ((currentPage - 1) * itemsPerPage + index)}

</td>
                  <td className="px-4 py-2 border whitespace-nowrap">{item.fullName}</td>
                  <td className="px-4 py-2 border">{item.dateOfAdmission}</td>
                  <td className="px-4 py-2 border">{item.admissionFor}</td>
                  <td className="px-4 py-2 border">{item.academicYear}</td>
                  <td className="px-4 py-2 border">{item.FatherName}</td>
                  <td className="px-4 py-2 border">{item.MotherName}</td>
                  <td className="px-4 py-2 border text-[#16bab1]">
                    {item.isApproved ? (
                      <span className="text-green-500">Approved</span>
                    ) : userRole === 'admin' ? (
                      <button
                        className={`text-blue-500 hover:text-blue-700 flex items-center gap-1 ${approving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => handleApprove(item.id)}
                        disabled={approving}
                      >
                        {approving ? <Loader /> : 'Approve'} <FaCheck />
                      </button>
                    ) : (
                      <span className="text-red-500">Pending</span>
                    )}
                  </td>
                  {userRole === 'admin' && (
                    <td className="px-4 py-2 border text-yellow-500">
                      <button
                        className="hover:text-yellow-700"
                        onClick={() => handlePromote(item)}
                      >
                        Promote
                      </button>
                    </td>
                  )}
                  <td className="px-4 py-2 border">
                    <div className="flex justify-center gap-3">
                      <button className="text-blue-500 hover:text-blue-700" onClick={() => handleView(item)}>
                        <FaEye />
                      </button>
                      <button className="text-purple-500 hover:text-purple-700" onClick={() => handlePrintSingleForm(item.id)}>
                        <FaPrint />
                      </button>
                      {userRole === 'admin' && (
                        <>
                          <button
                            className="text-green-500 hover:text-green-700"
                            onClick={() => handleEdit(item.id)}
                          >
                            <FaPencilAlt />
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              setFormToDelete(item.id);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Delete Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-bold">Confirm Deletion</h2>
            <p>Are you sure you want to delete this admission?</p>
            <div className="flex justify-end mt-4">
              <button
                className="bg-gray-300 text-black py-1 px-3 rounded mr-2"
                onClick={() => setShowDeleteDialog(false)}
              >
                No
              </button>
              <button
                className="bg-red-500 text-white py-1 px-3 rounded"
                onClick={handleDelete}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promote Dialog */}
      {showPromoteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-bold">Promote Admission</h2>
            <p>Please select the new class:</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {options.map((option) => (
                <div
                  key={option}
                  onClick={() => handleAdmissionForChange(option)}
                  className={`cursor-pointer capitalize px-4 py-1 rounded-full border transition-all duration-200 ${selectedAdmissionFor === option
                    ? 'bg-[#16bab1] text-white border-[#16bab1]'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#16bab1]'
                    }`}
                >
                  {option}
                </div>
              ))}
            </div>
            <input
              type="number"
              value={newFeeAmount}
              onChange={(e) => setNewFeeAmount(e.target.value)}
              className="border border-gray-300 p-2 rounded w-full mt-2"
              placeholder="New Fee Amount"
            />
            <select
              value={newAcademicYear}
              onChange={(e) => setNewAcademicYear(e.target.value)}
              className="border border-gray-300 p-2 rounded w-full mt-2 outline-none focus:ring-2 focus:ring-[#16bab1]"
            >
              <option value="">Select Academic Year</option>
              {academicYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Select the year to promote the student to.</p>
            <div className="flex justify-end mt-4">
              <button
                className="bg-gray-300 text-black py-1 px-3 rounded mr-2"
                onClick={() => setShowPromoteDialog(false)}
              >
                Cancel
              </button>
              <button
                className="bg-green-500 text-white py-1 px-3 rounded"
                onClick={confirmPromotion}
              >
                Promote
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Dialog */}
      {showApproveDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-bold">Confirm Approval</h2>
            <p>Please enter the fee amount:</p>
            <input
              type="number"
              value={feeAmount}
              onChange={(e) => setFeeAmount(e.target.value)}
              className="border border-gray-300 p-2 rounded w-full mt-2"
              placeholder="Fee Amount"
            />
            <div className="flex justify-end mt-4">
              <button
                className="bg-gray-300 text-black py-1 px-3 rounded mr-2"
                onClick={() => setShowApproveDialog(false)}
              >
                Cancel
              </button>
              <button
                className="bg-green-500 text-white py-1 px-3 rounded"
                onClick={confirmApproval}
                disabled={approving}
              >
                {approving ? <Loader /> : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default Table;